/* Emissões → Planilha padrão: o modelo da categoria preenchido, com as colunas como no modelo. */

import type { Planilha } from '../../core/analisePlanilha';
import { catBySlug } from '../../core/catalogo';
import { cat, state } from '../../core/estado';
import { esc, norm } from '../../core/formato';
import type { Categoria } from '../../tipos/api';
import { opcoesCategorias, temPadrao } from '../../ui/campos';
import { escLabel } from '../../ui/componentes';
import { ic, valorDe } from '../../ui/dom';
import { toast } from '../../ui/toast';
import { render } from '../../roteador';
import type { Pagina } from '../pagina';
import { baixarModelo, enviarImportacao, lerPlanilha, resultadoHtml } from './importacao';

/** Coluna de cada campo pelo rótulo do modelo (ou pelo nome técnico do campo). */
export function colunasDoModelo(c: Categoria, p: Planilha): { mapa: Record<string, string>; faltam: string[] } {
  const mapa: Record<string, string> = {};
  c.campos.forEach((x) => {
    const h = p.cabecalho.find((h) => h && [norm(x.rotulo), norm(x.nome)].includes(norm(h)));
    if (h) mapa[x.nome] = h;
  });
  const faltam = c.campos.filter((x) => x.obrigatorio && !temPadrao(x) && !mapa[x.nome] && x.tipo !== 'obra').map((x) => x.rotulo);
  return { mapa, faltam };
}

function conferenciaHtml(c: Categoria): string {
  const imp = state.imp;
  if (!imp.planilha) return '';
  const { mapa, faltam } = colunasDoModelo(c, imp.planilha);
  if (faltam.length) {
    return `<div class="callout warn" style="margin-top:18px">${ic('i-alert')}<div><b>Esta planilha não segue o modelo de ${esc(c.nome)}.</b><br>
      Faltam as colunas: ${faltam.map(esc).join(', ')}.<br>Baixe o modelo acima ou use a
      <a class="btn-plain" style="font-weight:700;text-decoration:underline" href="#/emissoes/personalizada">Planilha personalizada</a>, que lê a sua planilha do jeito que ela está.</div></div>`;
  }
  const extras = imp.planilha.cabecalho.filter((h) => h && !Object.values(mapa).includes(h));
  return `
      <div class="callout success" style="margin-top:18px">${ic('i-checkcircle')}<div><b>Planilha no padrão do modelo.</b><br>
        ${Object.keys(mapa).length} coluna(s) reconhecida(s)${extras.length ? `; ignoradas: ${extras.map(esc).join(', ')}` : ''}.</div></div>
      ${mapa.id_unidade ? '' : `<div class="field" style="margin-top:14px"><label for="impObra">Obra para todas as linhas <span class="req">*</span></label>
        <select class="inp" id="impObra"><option value="">Selecione…</option>${cat().obras.map((o) => `<option value="${esc(o.id)}" ${o.id === imp.obra ? 'selected' : ''}>${esc(o.nome)}</option>`).join('')}</select>
        <div class="hint">A planilha não tem a coluna Obra.</div></div>`}
      <button class="btn btn-primary" data-clique="importar" style="margin-top:6px">${ic('i-upload')} Importar ${imp.planilha.rows.length} linha(s)</button>`;
}

export const planilha: Pagina = {
  render() {
    const imp = state.imp;
    const c = catBySlug(imp.slug);
    imp.slug = c.slug;
    return `
  <h1 class="page">Planilha padrão</h1>
  <p class="sub">Baixe o modelo da categoria, preencha uma linha por lançamento e envie. Para usar uma planilha que você já tem, com outras colunas, vá em <a class="btn-plain" style="font-weight:700;text-decoration:underline" href="#/emissoes/personalizada">Planilha personalizada</a>.</p>
  <div class="grid g2" style="align-items:start">
    <div class="card">
      <h2 class="sec">1. Categoria e modelo</h2>
      <div class="field"><select class="inp" data-muda="categoria">${opcoesCategorias(c.slug)}</select>
        <div class="hint">${escLabel(c.escopo)} · ${esc(c.descricao)}</div></div>
      <button class="btn btn-ghost" data-clique="modelo">${ic('i-dl')} Baixar modelo desta categoria (.xlsx)</button>

      <h2 class="sec" style="margin-top:28px">2. Modelo preenchido</h2>
      <div class="drop ${imp.planilha ? 'has-files' : ''}" data-clique="escolher">
        ${ic('i-upload')}<div><u>Envie</u> a planilha aqui</div><span class="fmt">XLSX ou XLSM — até 20 MB</span>
      </div>
      <input type="file" id="filePlanilha" accept=".xlsx,.xlsm" style="display:none" data-muda="arquivo">
      ${imp.planilha ? `<div class="file-chip" style="margin-top:12px">${ic('i-doc')}${esc(imp.planilha.arquivo)}
        <small>aba "${esc(imp.planilha.aba)}" · ${imp.planilha.rows.length} linha(s)</small></div>` : ''}
      ${conferenciaHtml(c)}
    </div>
    <div style="display:flex;flex-direction:column;gap:16px">
      ${resultadoHtml(imp.resultado)}
      <div class="callout info">${ic('i-info')}
        <div><b>Como funciona?</b><br>Cada linha vira um lançamento da categoria e passa pelas mesmas validações do lançamento manual; o tCO₂e é calculado pelo motor da ZNIT ao gravar. Nas colunas de lista (combustível, material, resíduo, destino, tratamento, obra) vale o nome da lista ou uma descrição parecida. Se a importação parar numa linha com erro, as anteriores continuam gravadas.</div>
      </div>
    </div>
  </div>`;
  },

  acoes: {
    categoria(el) {
      state.imp.slug = (el as HTMLSelectElement).value;
      state.imp.resultado = null;
      render();
    },

    escolher: () => document.getElementById('filePlanilha')?.click(),

    async arquivo(el) {
      const file = (el as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        Object.assign(state.imp, { planilha: await lerPlanilha(file), file, resultado: null });
        render();
      } catch (e) {
        toast((e as Error).message || 'Não foi possível ler a planilha.');
      }
    },

    async modelo() {
      try {
        await baixarModelo(catBySlug(state.imp.slug));
      } catch (e) {
        toast((e as Error).message);
      }
    },

    async importar(el) {
      const imp = state.imp;
      const c = catBySlug(imp.slug);
      if (!imp.planilha || !imp.file) return;
      const { mapa, faltam } = colunasDoModelo(c, imp.planilha);
      if (faltam.length) return;
      const fixos: Record<string, string> = {};
      if (!mapa.id_unidade) {
        imp.obra = valorDe('impObra');
        if (!imp.obra) return toast('Escolha a obra.');
        fixos.id_unidade = imp.obra;
      }
      const indice = Object.fromEntries(imp.planilha.cabecalho.map((h, i) => [h, i]));
      el.classList.add('dis');
      imp.resultado = await enviarImportacao({
        categoria: c, arquivo: imp.planilha.arquivo, file: imp.file, mapa, fixos,
        aba: imp.planilha.aba, linhaCab: imp.planilha.linhaCab,
        linhas: imp.planilha.rows.map((r) => ({
          n: r.n,
          dados: Object.fromEntries(Object.entries(mapa).map(([campo, col]) => [campo, r.l[indice[col]]])
            .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')),
        })),
      });
      if (imp.resultado.ok) toast(`${imp.resultado.n} lançamento(s) importado(s).`);
      render();
    },
  },
};
